/**
 * @fileoverview TourFilter.jsx
 * 
 * © 2026 KITH Travels (Pvt) Ltd. All rights reserved.
 * This software is the confidential and proprietary information of KITH Travels.
 * You shall not disclose such Confidential Information and shall use it only in 
 * accordance with the terms of the license agreement you entered into with KITH Travels.
 */
import React from 'react'
import { Categories } from '../../assets/Assets'
import { useTranslation } from 'react-i18next'

function FilterPill({ label, isActive, onClick }) {
    return (
        <button 
            onClick={onClick}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all border ${
                isActive 
                ? 'bg-KITH-primary text-white border-KITH-primary shadow-md' 
                : 'bg-white text-gray-500 border-gray-100 hover:border-KITH-primary/30 hover:text-KITH-primary'
            }`}
        >
            {label}
        </button>
    );
}

function TourFilter({ 
    selectedCategory, setSelectedCategory, 
    selectedDuration, setSelectedDuration,
    selectedPrice, setSelectedPrice 
}) {
  const { t } = useTranslation();
  return (
    <div className='flex flex-col gap-8 bg-white p-8 rounded-[2rem] shadow-xl border border-gray-50 w-full max-w-5xl'>
        {/* Category Filter */}
        <div className='flex flex-col gap-3'>
            <label className='text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1'>{t('tour_filter.explore_category')}</label>
            <div className='flex flex-wrap gap-2'>
                <FilterPill 
                    label={t('tour_filter.all')} 
                    isActive={selectedCategory === 'All'} 
                    onClick={() => setSelectedCategory('All')} 
                />
                {Categories.map((cat, idx) => (
                    <FilterPill 
                        key={idx} 
                        label={t(`categories.${cat}`)} 
                        isActive={selectedCategory === cat} 
                        onClick={() => setSelectedCategory(cat)} 
                    />
                ))}
            </div>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-8'>
            {/* Price Range Filter */}
            <div className='flex flex-col gap-3'>
                <label className='text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1'>{t('tour_filter.budget_range')}</label>
                <div className='flex flex-wrap gap-2'>
                    {[
                        { label: t('tour_filter.all'), value: 'All' },
                        { label: t('tour_filter.price_under_1k'), value: '0-1000' },
                        { label: t('tour_filter.price_1k_2_5k'), value: '1000-2500' },
                        { label: t('tour_filter.price_2_5k_plus'), value: '2500+' }
                    ].map((price, idx) => (
                        <FilterPill 
                            key={idx} 
                            label={price.label} 
                            isActive={selectedPrice === price.value} 
                            onClick={() => setSelectedPrice(price.value)} 
                        />
                    ))}
                </div>
            </div>

            {/* Duration Filter */}
            <div className='flex flex-col gap-3'>
                <label className='text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1'>{t('tour_filter.duration')}</label>
                <div className='flex flex-wrap gap-2'>
                    {[
                        { label: t('tour_filter.all'), value: 'All' },
                        { label: t('tour_filter.dur_short'), value: '1-3' },
                        { label: t('tour_filter.dur_med'), value: '4-7' },
                        { label: t('tour_filter.dur_long'), value: '8+' }
                    ].map((dur, idx) => (
                        <FilterPill 
                            key={idx} 
                            label={dur.label} 
                            isActive={selectedDuration === dur.value} 
                            onClick={() => setSelectedDuration(dur.value)} 
                        />
                    ))}
                </div>
            </div>
        </div>
    </div>
  )
}

export default TourFilter
